<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <b-row>
        <b-col md="4">
          <FormulateInput
            v-model="filters.storeId"
            type="vue-select"
            :label="$t('Loja')"
            :placeholder="$t('Todas')"
            :options="activeStoreOptions"
          />
        </b-col>
        <b-col md="5">
          <e-search-sku
            id="sku"
            v-model="filters.skuId"
            :required="false"
            :min-search-length="3"
            :include-fixed-assets="true"
          />
        </b-col>
      </b-row>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-table
        ref="tablestockItems"
        show-empty
        striped
        class="mb-1 bordered"
        :empty-text="getEmptyTableMessage($tc('STOCK_POSITION.NAME'), 'female', queryExecuted)"
        :fields="fields"
        :items="stockItems"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(skuName)="{ item }">
          <e-product-router-link
            :sku-id="item.sku.id"
            :product-id="item.sku.productId"
            :product-name="item.sku.name"
            :ean="item.sku.ean"
            :show-stock-position-link="false"
          />
        </template>
        <template #cell(paidReservation)="{ item }">
          <e-router-link
            v-if="item.hasStock == null && item.paidReservation"
            :to="getPendingDeliveryOrderListRoute(item, item.sku.id)"
            :title="$t('Visualizar pedidos')"
            :value="formatQuantity(item, item.paidReservation)"
          />
          <span v-else>{{ formatQuantity(item, item.paidReservation) }}</span>
        </template>
        <template #cell(awaitingReturn)="{ item }">
          <e-router-link
            v-if="item.hasStock == null && item.awaitingReturn"
            :to="getReclaimListRoute(item, item.sku.id)"
            :title="$t('Retorno de produtos')"
            :value="formatQuantity(item, item.awaitingReturn)"
          />
          <span v-else>{{ formatQuantity(item, item.awaitingReturn) }}</span>
          <small
            v-for="asset in item.lendingAssets || []"
            :key="asset.skuId"
            class="d-block text-muted"
          >
            <e-instructions
              :target="`lending_asset_awaitingReturn_${item.store.id}_${item.sku.id}_${asset.skuId}`"
              :title="asset.name"
              :instruction="
                $t('Quantidade deste comodato que está com os clientes e que foi levada junto com este produto.')
              "
              container-class="mr-25"
            />
            {{ $t('Associado') }}:
            <span class="d-block">
              <e-router-link
                v-if="item.hasStock == null && asset.awaitingReturn"
                :to="getReclaimListRoute(item, item.sku.id)"
                :title="$t('Retorno de produtos')"
                :value="formatQuantity(item, asset.awaitingReturn)"
              />
              <template v-else>{{ formatQuantity(item, asset.awaitingReturn) }}</template>
            </span>
          </small>
        </template>
        <template #cell(stock)="row">
          <span v-if="row.item.hasStock != null">
            {{ row.item.hasStock ? $t('Sim') : $t('Não') }}
          </span>
          <template v-else>
            <router-link
              :to="{
                name: 'stock-movement-list',
                query: { storeId: row.item.store.id, skuId: row.item.sku.id },
              }"
              :title="$t('Visualizar movimentações')"
              class="hyperlink"
            >
              <feather-icon icon="ExternalLinkIcon" />
              {{ row.item.stock }}
            </router-link>

          </template>
        </template>
        <template #cell(reservation)="row">
          <span v-if="row.item.hasStock != null">
            -
          </span>
          <template v-else>
            <e-router-link
              v-if="row.item.reservation"
              :to="{
                name: 'stock-reservation-list',
                query: { storeId: row.item.store.id, skuId: row.item.sku.id },
              }"
              :title="$t('Visualizar reservas')"
              :value="row.item.reservation"
            />
            <span v-else>0</span>
          </template>
        </template>
      </b-table>

      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="user-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>
  </div>
</template>

<script>
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import {
  EPageSizeSelector,
  EPaginationSummary,
  EFilters,
  ESearchSku,
  ERouterLink,
  EProductRouterLink,
  EInstructions,
} from '@/views/components'
import { formulateValidation, statusTypes } from '@/mixins'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    EFilters,
    ESearchSku,
    ERouterLink,
    EProductRouterLink,
    EInstructions,
  },

  mixins: [formulateValidation, statusTypes],

  data() {
    return {
      queryExecuted: false,
      fetching: false,
      saving: false,
    }
  },

  computed: {
    ...mapGetters('app', ['activeStoreOptions']),
    ...mapState('pages/stock/stockPosition', ['stockItems', 'paging', 'sorting', 'filters']),

    fields() {
      return [
        {
          label: this.$t('Data/Hora'),
          key: 'modifiedDate',
          class: 'text-center',
          thStyle: { width: '150px' },
          sortable: true,
          formatter: (value, index, item) =>
            this.$options.filters.datetime(value || item.createdDate),
        },
        {
          label: this.$t('Loja'),
          key: 'store',
          tdClass: 'text-left',
          thStyle: { width: '250px' },
          formatter: val => this.$options.filters.storeName(val),
        },
        {
          label: this.$t('Produto'),
          key: 'skuName',
          sortKey: 'sku.name',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('Ativos (qtd. original)'),
          key: 'originalAssets',
          class: 'text-center',
          thStyle: { width: '120px' },
          formatter: (value, key, item) => this.formatQuantity(item, value),
        },
        {
          label: this.$t('Reservado (pago, não entregue)'),
          key: 'paidReservation',
          class: 'text-center',
          thStyle: { width: '120px' },
        },
        {
          label: this.$t('Comodato/Aluguel em aberto'),
          key: 'awaitingReturn',
          class: 'text-center',
          thStyle: { width: '120px' },
        },
        {
          label: this.$t('Reservado'),
          key: 'reservation',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
          formatter: (value, index, item) => item.hasStock != null ? '-' : this.$options.filters.number(value, 3, true),
        },
        {
          label: this.$t('Em estoque'),
          key: 'stock',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
          // eslint-disable-next-line no-nested-ternary
          formatter: (value, index, item) => item.hasStock != null ? (item.hasStock ? this.$t('Sim') : this.$t('Não')) : this.$options.filters.number(value, 3, true),
        },
        {
          label: this.$t('Disponível'),
          key: 'available',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
          formatter: (value, index, item) =>
            // eslint-disable-next-line no-nested-ternary
            item.hasStock != null ? (item.hasStock ? this.$t('Sim') : this.$t('Não')) : this.$options.filters.number(item.stock - (item.reservation || 0), 3, true),
        },
      ]
    },

    dateToFormated() {
      return this.$options.filters.date(this.filters.dateTo)
    },
    today() {
      return new Date()
    },
  },

  mounted() {
    if (this.$route.query?.storeId || this.$route.query?.skuId) {
      this.setFilters({
        storeId: this.$route.query?.storeId?.toString(),
        skuId: this.$route.query?.skuId?.toString(),
      })
      this.getData()
    }
  },

  methods: {
    ...mapActions('pages/stock/stockPosition', [
      'fetchStockItems',
      'resetFilters',
      'setCurrentPage',
      'setPageSize',
      'setFilters',
    ]),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchStockItems()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    formatQuantity(item, value) {
      if (item.hasStock != null || value == null) return '-'
      return this.$options.filters.number(value, 3, true)
    },

    getPendingDeliveryOrderListRoute(item, skuId) {
      const { PENDING, PREPARING, IN_PROGRESS } = this.deliveryStatusEnum
      return {
        name: 'order-list',
        query: {
          storeId: item.store.id,
          skuId,
          status: Object.values(this.saleStatusEnum).filter(
            status => status !== this.saleStatusEnum.CANCELED
          ),
          deliveryStatus: [PENDING, PREPARING, IN_PROGRESS],
        },
      }
    },

    getReclaimListRoute(item, skuId) {
      return {
        name: 'reclaim-list',
        query: { storeId: item.store.id, skuId, startDate: null, endDate: null },
      }
    },

    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },
  },
}
</script>

<style lang="scss" scoped></style>
